import type { FragmentType } from "@repo/graphql-types/fragment-masking";
import { useFragment } from "@repo/graphql-types/fragment-masking";
import { graphql } from "@repo/graphql-types/gql";
import { useMemo, useState } from "react";
import { useRouteContext, useSearch } from "@tanstack/react-router";
import { AssistBillingTypes } from "@repo/lib";
import { BillingFooter } from "@/components/billing-footer";
import { BillingHeader } from "@/components/billing-header";
import type { BillingItemQueryFragment } from "@/components/billing-item";
import { BillingItemList } from "@/components/billing-item-list";
import { BillingFilter } from "@/components/billing-filter";
import { BillingListEmptyState } from "@/components/billing-list-empty-state";

export const BillingListQueryFragment = graphql(/* GraphQL */ `
  fragment BillingListQueryFragment on CobrancaExibicao {
    codCobranca
    valor
    dataVencimento
    movimentacoes {
      ...BillingListItemQueryFragment
    }
  }
`);

export const BillingList = (props: {
  billingList: FragmentType<typeof BillingListQueryFragment>[];
}): JSX.Element => {
  const { flags } = useRouteContext({ strict: false });
  const params = useSearch({ from: "/settings/billing/" });

  const shouldDisplayFilterButton = useMemo(
    () => flags["exibe-filtro-no-historico-de-uso"],
    [flags],
  );

  const billingListFragment = useFragment(BillingListQueryFragment, props.billingList);
  const billingListOrdered = Object.values(billingListFragment).sort((a, b) => {
    return new Date(a.dataVencimento ?? "").getTime() <
      new Date(b.dataVencimento ?? "").getTime()
      ? 1
      : -1;
  });

  const billingList = useMemo(() => billingListOrdered, [billingListOrdered]);
  const [currentBillingIndex, setCurrentBillingIndex] = useState<number>(0);

  const [billingItens, setBillingItens] = useState<
    FragmentType<typeof BillingItemQueryFragment>[]
  >(
    billingList[currentBillingIndex].movimentacoes as FragmentType<
      typeof BillingItemQueryFragment
    >[],
  );

  const showBillingItensByBilling = (billingIndex: number): void => {
    setCurrentBillingIndex(billingIndex);
    setBillingItens(
      billingList[billingIndex].movimentacoes as FragmentType<
        typeof BillingItemQueryFragment
      >[],
    );
  };

  const filteredBillingItems = useMemo(
    () =>
      filterBillingItemsByBillingType(
        billingItens as BillingItemWithType[],
        params.selectedFilter as string | undefined,
      ),
    [billingItens, params.selectedFilter],
  );

  const filteredBillingItemsTotalValue = params.selectedFilter
    ? filteredBillingItems.reduce(
        (
          total,
          item: FragmentType<typeof BillingItemQueryFragment> & {
            valor?: number;
          },
        ) => total + (item.valor ?? 0),
        0,
      )
    : (billingList[currentBillingIndex]?.valor as number);

  return (
    <div className="mx-auto w-full h-full overflow-hidden">
      <BillingHeader
        billingList={billingList}
        currentBillingIndex={currentBillingIndex}
        showBillingItensByBilling={showBillingItensByBilling}
      />

      {shouldDisplayFilterButton ? <BillingFilter /> : null}

      <div className="h-[85%] overflow-y-scroll scrollbar-hidden">
        {filteredBillingItems.length > 0 ? (
          <BillingItemList
            billingItens={filteredBillingItems}
            currentBillingIndex={currentBillingIndex}
          />
        ) : (
          <BillingListEmptyState />
        )}
      </div>

      <BillingFooter total={filteredBillingItemsTotalValue} />
    </div>
  );
};

type BillingItemWithType = FragmentType<typeof BillingItemQueryFragment> & {
  identificacaoTipoMovimentacao?: string;
};

function filterBillingItemsByBillingType(
  items: BillingItemWithType[],
  selectedFilter: string | undefined | null,
): BillingItemWithType[] {
  if (!selectedFilter || selectedFilter === "default") {
    return items;
  }

  if (selectedFilter === "assist") {
    const assistTypes = Object.values(AssistBillingTypes);
    return items.filter(
      (item) =>
        typeof item.identificacaoTipoMovimentacao === "string" &&
        assistTypes.includes(item.identificacaoTipoMovimentacao as AssistBillingTypes),
    );
  }

  return items.filter((item) => {
    const billingType = item.identificacaoTipoMovimentacao;

    return (
      typeof billingType === "string" &&
      billingType.toLowerCase().includes(String(selectedFilter).toLowerCase())
    );
  });
}
